import { useState } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import type { FeatureRow } from "@/infrastructure/supabase/database.types";
import type { Domain } from "@/presentation/data/domains";
import {
  VERDICT_COLOR,
  VERDICT_LABEL,
  verdictsForBranch,
  type RoadmapBranch,
  type Verdict,
} from "@/presentation/data/roadmaps";

interface EditVerdictDialogProps {
  branch: RoadmapBranch;
  feature: FeatureRow | null;
  currentVerdict: Verdict | null;
  currentNotes: string | null;
  onSave: (input: { verdict: Verdict; notes: string | null }) => void;
  onRemove: (feature: FeatureRow) => void;
  onClose: () => void;
}

export function EditVerdictDialog({
  branch,
  feature,
  currentVerdict,
  currentNotes,
  onSave,
  onRemove,
  onClose,
}: EditVerdictDialogProps) {
  return (
    <Dialog open={feature !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        {feature && currentVerdict ? (
          <EditVerdictBody
            key={`${feature.id}-${currentVerdict}`}
            branch={branch}
            feature={feature}
            currentVerdict={currentVerdict}
            currentNotes={currentNotes}
            onSave={onSave}
            onRemove={() => onRemove(feature)}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function EditVerdictBody({
  branch,
  feature,
  currentVerdict,
  currentNotes,
  onSave,
  onRemove,
  onClose,
}: {
  branch: RoadmapBranch;
  feature: FeatureRow;
  currentVerdict: Verdict;
  currentNotes: string | null;
  onSave: (input: { verdict: Verdict; notes: string | null }) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [verdict, setVerdict] = useState<Verdict>(currentVerdict);
  const [notes, setNotes] = useState<string>(currentNotes ?? "");
  const domain = feature.domain as Domain | null;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit verdict</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="bg-muted/40 rounded-md border px-3 py-2">
          <p className="text-foreground text-sm font-medium">{feature.title}</p>
          {feature.subtitle ? (
            <p className="text-foreground/70 mt-0.5 text-xs leading-snug">{feature.subtitle}</p>
          ) : null}
          {domain ? (
            <div className="mt-1">
              <DomainPill domain={domain} size="sm" />
            </div>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <label className="text-foreground text-xs font-medium">Verdict</label>
          <VerdictPicker branch={branch} value={verdict} onChange={setVerdict} />
        </div>
        <div className="space-y-1.5">
          <label className="text-foreground text-xs font-medium">Notes</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional context — what changes, what the off-the-shelf candidate is, …"
          />
        </div>
      </div>
      <DialogFooter className="!justify-between">
        <Button variant="ghost" onClick={onRemove} className="text-destructive">
          <Trash2 className="size-3.5" />
          Send back to stack
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ verdict, notes: notes.length > 0 ? notes : null })}
            style={{ background: VERDICT_COLOR[verdict] }}
          >
            Save
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function VerdictPicker({
  branch,
  value,
  onChange,
}: {
  branch: RoadmapBranch;
  value: Verdict;
  onChange: (v: Verdict) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {verdictsForBranch(branch).map((v) => {
        const active = value === v;
        const color = VERDICT_COLOR[v];
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className="rounded-md border p-2 text-center text-[12px] font-medium transition-all"
            style={
              active
                ? { borderColor: color, background: `${color}1a`, color }
                : { borderColor: "oklch(0.91 0.008 240)" }
            }
          >
            {VERDICT_LABEL[v]}
          </button>
        );
      })}
    </div>
  );
}
